import type { Terrain } from '../game/Terrain';
import { resolveObstacles, type Obstacle } from './Collision';

export interface RolyPolyState {
  x: number;
  y: number;
  z: number;
  heading: number;
  speed: number;
  /** 'walk' = crawling forward, 'pause' = stopped while turning. */
  mode: 'walk' | 'pause';
  /** Seconds left in the current mode. */
  modeT: number;
}

export interface RolyPolySimOptions {
  count?: number;
  patrolRadius?: number;
  obstacles?: readonly Obstacle[];
  nearbyObstacles?: (x: number, z: number) => readonly Obstacle[];
}

const WALK_SPEED = 0.12;
// Long walks between course changes — roly-polies meander, not zigzag.
const WALK_MIN = 6.0;
const WALK_MAX = 14.0;
// While turning they stop completely for a beat before committing to the
// new heading, which hides any snap and reads as "thinking about it".
const PAUSE_MIN = 0.8;
const PAUSE_MAX = 1.5;
const TURN_JITTER = 1.4; // radians

/**
 * Roly-polies are peaceful, indestructible pillbugs. They just wander inside
 * the patrol ring. Nothing in the world can harm them — no callbacks accept
 * damage or kill requests here on purpose.
 */
export class RolyPolySim {
  readonly rolies: RolyPolyState[] = [];
  private readonly patrolRadius: number;
  private readonly obstacles: readonly Obstacle[];
  private readonly nearbyObstacles: ((x: number, z: number) => readonly Obstacle[]) | null;

  constructor(private terrain: Terrain, opts: RolyPolySimOptions = {}) {
    const count = opts.count ?? 5;
    this.patrolRadius = opts.patrolRadius ?? 30;
    this.obstacles = opts.obstacles ?? [];
    this.nearbyObstacles = opts.nearbyObstacles ?? null;

    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = 4 + Math.random() * (this.patrolRadius - 4);
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;
      this.rolies.push({
        x,
        y: terrain.getHeight(x, z),
        z,
        heading: Math.random() * Math.PI * 2,
        speed: WALK_SPEED,
        mode: 'walk',
        modeT: WALK_MIN + Math.random() * (WALK_MAX - WALK_MIN),
      });
    }
  }

  update(dt: number): void {
    for (let i = 0; i < this.rolies.length; i++) {
      const r = this.rolies[i];

      r.modeT -= dt;
      if (r.modeT <= 0) {
        if (r.mode === 'walk') {
          r.mode = 'pause';
          r.modeT = PAUSE_MIN + Math.random() * (PAUSE_MAX - PAUSE_MIN);
          r.speed = 0;
        } else {
          // Finished the stop — commit to a fresh heading and start walking.
          r.heading += (Math.random() - 0.5) * TURN_JITTER;
          r.mode = 'walk';
          r.modeT = WALK_MIN + Math.random() * (WALK_MAX - WALK_MIN);
          r.speed = WALK_SPEED;
        }
      }

      if (r.mode === 'walk') {
        r.x += Math.cos(r.heading) * r.speed * dt;
        r.z += Math.sin(r.heading) * r.speed * dt;
        if (this.obstacles.length > 0) resolveObstacles(r, 0.06, this.obstacles);
        if (this.nearbyObstacles) resolveObstacles(r, 0.06, this.nearbyObstacles(r.x, r.z));
      }

      // Stay inside the patrol ring.
      const dist = Math.hypot(r.x, r.z);
      if (dist > this.patrolRadius) {
        const k = this.patrolRadius / dist;
        r.x *= k;
        r.z *= k;
        // Ring bounce also forces a pause-then-turn to avoid the snap.
        r.heading = Math.atan2(-r.z, -r.x);
        r.mode = 'pause';
        r.modeT = PAUSE_MIN + Math.random() * (PAUSE_MAX - PAUSE_MIN);
        r.speed = 0;
      }
      r.y = this.terrain.getHeight(r.x, r.z);
    }
  }
}
